'use client';

import { useEffect, useState } from 'react';

import { getProceduresBySlugs } from '@/app/(main)/(pages)/busca/[slug]/_services/get-procedures-by-slugs.service';
import { useHydrateUserLocation } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-hydrate-user-location.hook';
import { useSanitizeDateFilterParams } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-sanitize-date-filter-params.hook';
import { useEnsureCartLoaded } from '@/hooks/use-ensure-cart-loaded.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import SearchHeader from '@/app/(main)/(pages)/busca/[slug]/_components/search-header/search-header.component';
import SearchSchedules from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-schedules.component';
import SearchHeaderLoading from '@/app/(main)/(pages)/busca/[slug]/_components/search-header/search-header-loading.component';
import SearchCardLoading from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card-loading-component';
import { CodesOrSlugsResponse } from '@/app/(main)/(pages)/busca/[slug]/_types/codes-or-slugs-response.type';
import SearchNotFound from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-not-found';
import SearchError from '@/app/(main)/(pages)/busca/[slug]/_components/search-error/search-error.component';
import {
  classifySearchError,
  isRetryableSearchError,
  SearchErrorType,
} from '@/app/(main)/(pages)/busca/[slug]/_utils/search-error.util';
import { getLogger } from '@/logger/logger';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { useLegacySearchRedirectUrl } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-legacy-search-redirect.hook';

interface SearchWrapperProps {
  slug: string;
  searchParams: { [key: string]: string | string[] | undefined };
}

export function SearchWrapper({ slug, searchParams }: SearchWrapperProps) {
  const { isLoading: isPageLoading, startLoading: startPageLoading, stopLoading: stopPageLoading } = useLoading(true);
  const [procedures, setProcedures] = useState<CodesOrSlugsResponse>([]);
  const [errorType, setErrorType] = useState<SearchErrorType | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const { value: newScheduleEnabled, loading: isFlagLoading } = useFeatureFlag('newSchedule');
  const buildLegacyRedirectUrl = useLegacySearchRedirectUrl();

  const isLaboratorialSearch = slug === 'exames-laboratoriais';
  const isVaccineSearch = slug === 'vacina';

  const examsParam = searchParams.exames;
  const examsKey = Array.isArray(examsParam) ? examsParam.join(',') : examsParam ?? '';

  const { isLocationReady } = useHydrateUserLocation();

  useSanitizeDateFilterParams();

  useEnsureCartLoaded();

  const retry = () => setRetryToken((token) => token + 1);

  useEffect(() => {
    if (isFlagLoading || newScheduleEnabled) {
      return;
    }

    const filters = Object.fromEntries(
      Object.entries(searchParams).filter(([, value]) => value !== undefined),
    ) as Record<string, string | string[]>;

    window.location.replace(buildLegacyRedirectUrl(slug, filters));
  }, [isFlagLoading, newScheduleEnabled, slug, searchParams, buildLegacyRedirectUrl]);

  useEffect(() => {
    if (isFlagLoading || !newScheduleEnabled) {
      return;
    }

    async function fetchProcedures() {
      startPageLoading();
      setErrorType(null);

      try {
        const procedures = await getProceduresBySlugs(slug, { exames: examsKey || undefined });
        setProcedures(procedures);
      } catch (error) {
        const logger = getLogger('search.wrapper');
        const type = classifySearchError(error);
        logger.error('Failed to fetch procedures for search results', {
          search: { event: 'procedures_failed', type, slug, exams_key: examsKey },
          error,
        });
        setErrorType(type);
        setProcedures([]);
      } finally {
        stopPageLoading();
      }
    }

    fetchProcedures();
  }, [examsKey, slug, isFlagLoading, newScheduleEnabled, retryToken, startPageLoading, stopPageLoading]);

  if (isFlagLoading || !newScheduleEnabled || isPageLoading || !isLocationReady) {
    return (
      <>
        <SearchHeaderLoading />
        <SearchCardLoading />
      </>
    );
  }

  if (isRetryableSearchError(errorType)) {
    return <SearchError type={errorType} onRetry={retry} />;
  }

  if (!procedures.length) {
    return <SearchNotFound />;
  }

  return (
    <>
      <SearchHeader
        procedures={procedures}
        isLaboratorialSearch={isLaboratorialSearch}
        isVaccineSearch={isVaccineSearch}
      />

      <SearchSchedules
        procedures={procedures}
        isLaboratorialSearch={isLaboratorialSearch}
        isVaccineSearch={isVaccineSearch}
      />
    </>
  );
}
